/**
 * One place that decides where an alert, notification or room reference
 * should take the user, so the dashboard, alerts page, bell dropdown and
 * popup all behave the same way.
 *
 *  - alert with a sensor  -> sensor detail, chart focused on the alert time
 *  - alert with a room    -> dashboard filtered to that room
 *  - notification         -> alerts page with that alert expanded
 */

export interface AlertLike {
  sensor_id?: number | null;
  room_id?: number | null;
  created_at?: string | null;
}

/** Path to the sensor page with the chart focused on `at` (ISO timestamp). */
export function sensorFocusPath(sensorId: number, at?: string | null): string {
  return at ? `/sensor/${sensorId}?at=${encodeURIComponent(at)}` : `/sensor/${sensorId}`;
}

/** Dashboard filtered to one room. */
export function roomDashboardPath(roomId: number): string {
  return `/?room=${roomId}`;
}

/** Alerts page with one alert expanded and scrolled into view. */
export function alertsPagePath(alertId: number): string {
  return `/alerts?alert=${alertId}`;
}

/** Hub page with one hub expanded. */
export function hubPagePath(hubId: number): string {
  return `/hubs?hub=${hubId}`;
}

/**
 * Best destination for an alert record. Returns null when the alert carries
 * neither a sensor nor a room (nothing sensible to open).
 */
export function alertDetailPath(alert: AlertLike): string | null {
  if (alert.sensor_id) return sensorFocusPath(alert.sensor_id, alert.created_at);
  if (alert.room_id) return roomDashboardPath(alert.room_id);
  return null;
}

/**
 * Pick the smallest preset time range (hours) on the sensor page that still
 * contains `at`, with ~20 % margin so the marker is not glued to the left edge.
 * Falls back to 24 h when the timestamp is missing or unparseable.
 */
export const SENSOR_RANGE_PRESETS_H = [1, 6, 12, 24, 168, 720, 2160, 4380, 8760];

export function rangeContaining(atMs: number | null | undefined, nowMs: number = Date.now()): number {
  if (atMs == null || !Number.isFinite(atMs)) return 24;
  const ageH = Math.max(0, (nowMs - atMs) / 3600000) * 1.2;
  for (const h of SENSOR_RANGE_PRESETS_H) if (h >= ageH) return h;
  return SENSOR_RANGE_PRESETS_H[SENSOR_RANGE_PRESETS_H.length - 1];
}
